import React, { useMemo, useState, useEffect } from 'react'
import Icon from 'components/elements/Icon'

import { useActionless, useAppSelector } from 'redux/hooks'
import { TOGGLE_MODAL } from 'redux/active/actions/types'

import { CardType } from 'types/deck'
import {
  PRICE_SOURCE,
  PRICE_COLORS,
  PRICE_ICON_NAMES,
  HUMAN_PRICE_SOURCE,
  PRICE_CODES,
  PRICE_NAMES,
  PriceSourceType,
} from 'types/active'
import { PriceSources } from 'services/apiTypes/card.types'

import Modal from 'components/elements/Modal'
import Accordion from 'components/elements/Accordion'
import PhatInput from 'components/formElements/PhatInput'
import CursorCardWrapper from 'components/card/CursorCardWrapper'
import PhatButton from 'components/formElements/PhatButton'
import BuyCardsLink from 'components/formElements/BuyCardsLink'
import SelectMultipleCards from 'components/formElements/SelectMultipleCards'
import BuyFromMultipleVendors from 'components/formElements/BuyCardsFromMultipleVendors'

import { getPricesForCards, getPriceStringFromFloat } from 'utils/Price'

import styles from './buyBulkModal.module.scss'

type Props = {
  preSelectedCards?: CardType[]
  open?: boolean
  onClose?: () => void
  linkedFrom?: string
}

const BuyBulkModal = ({
  preSelectedCards,
  open: parentOpen,
  onClose: onParentClose,
  linkedFrom = 'bulkBuy',
}: Props) => {
  const reduxOpen = useAppSelector(state => state.active.modals.bulkBuy)

  const [toggleModal] = useActionless(TOGGLE_MODAL)

  const [cards, setSelectedCards] = useState<CardType[]>(preSelectedCards || [])
  const [replacedQuantities, setReplacedQuantities] = useState<Record<string, number>>({})

  useEffect(() => {
    if (!open) return

    setSelectedCards(preSelectedCards?.filter(card => !card.customCard) || [])
  }, [preSelectedCards])

  const { selectedCards, prices } = useMemo(() => {
    const filteredCards = cards.filter(card => !card.customCard)

    return {
      selectedCards: filteredCards.map(c => ({ ...c, qty: replacedQuantities[c.id] || c.qty })),
      prices: getPricesForCards(filteredCards),
    }
  }, [cards, replacedQuantities])

  const handleUpdatedCustomQuantity = (id: string, qty: number) => {
    setReplacedQuantities({ ...replacedQuantities, [id]: qty || 1 })
  }

  const selectedCardIds = selectedCards.map(card => card.id)

  return (
    <Modal
      open={parentOpen ?? reduxOpen}
      onClose={() => {
        if (onParentClose) return onParentClose()

        toggleModal({ modalName: 'bulkBuy', flag: false })
      }}
      closeButton
      modalClassName={styles.modal}>
      <div className={styles.content}>
        <h3>Export to retailers</h3>

        {!preSelectedCards && (
          <SelectMultipleCards onChange={setSelectedCards} className={styles.cardSelector} filterOutCustomCards />
        )}

        <h3>Buy exact printings in deck</h3>
        <BuyCardsLink priceSource={PRICE_SOURCE.MP} cards={cards} linkedFrom={linkedFrom}>
          <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['6'] }}>
            <Icon className={PRICE_ICON_NAMES['6']} />
            Mana Pool {prices.mp.totalPrice}
          </PhatButton>
        </BuyCardsLink>

        <h3>Buy cheapest printings</h3>
        <div className={styles.outboundLinks}>
          <BuyCardsLink priceSource={PRICE_SOURCE.CK} cards={cards} linkedFrom={linkedFrom}>
            <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['1'] }}>
              <Icon className={PRICE_ICON_NAMES['1']} />
              Card Kingdom {prices.ck.totalMinimumPrice}
            </PhatButton>
          </BuyCardsLink>

          <BuyCardsLink priceSource={PRICE_SOURCE.TCG} cards={cards} linkedFrom={linkedFrom}>
            <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['2'] }}>
              <Icon className={PRICE_ICON_NAMES['2']} />
              TCG Player {prices.tcg.totalMinimumPrice}
            </PhatButton>
          </BuyCardsLink>

          <BuyCardsLink priceSource={PRICE_SOURCE.SCG} cards={cards} linkedFrom={linkedFrom}>
            <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['5'] }}>
              <Icon className={PRICE_ICON_NAMES['5']} />
              Star City Games {prices.scg.totalMinimumPrice}
            </PhatButton>
          </BuyCardsLink>

          <BuyCardsLink priceSource={PRICE_SOURCE.MP} cards={cards} linkedFrom={linkedFrom}>
            <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['6'] }}>
              <Icon className={PRICE_ICON_NAMES['6']} />
              Mana Pool {prices.mp.totalMinimumPrice}
            </PhatButton>
          </BuyCardsLink>

          <BuyCardsLink priceSource={PRICE_SOURCE.MTGO} cards={cards} linkedFrom={linkedFrom}>
            <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['3'] }}>
              <Icon className={PRICE_ICON_NAMES['3']} />
              Cardhoarder {prices.mtgo.totalMinimumPrice}
            </PhatButton>
          </BuyCardsLink>

          <BuyCardsLink priceSource={PRICE_SOURCE.TCGLAND} cards={cards} linkedFrom={linkedFrom}>
            <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['7'] }}>
              <Icon className={PRICE_ICON_NAMES['7']} />
              TCG Land {prices.tcgLand.totalMinimumPrice} (MXN)
            </PhatButton>
          </BuyCardsLink>
        </div>

        <h3>Buy from multiple sources</h3>
        <BuyFromMultipleVendors cards={cards} linkedFrom={linkedFrom} />

        <h3>Sell links</h3>
        <div>
          <BuyCardsLink priceSource={PRICE_SOURCE.CK} cards={cards} linkedFrom={`${linkedFrom}_sell`} isSellLink>
            <PhatButton className={styles.retailerButton} style={{ backgroundColor: PRICE_COLORS['1'] }}>
              <Icon className={PRICE_ICON_NAMES['1']} />
              Sell on Card Kingdom
            </PhatButton>
          </BuyCardsLink>
        </div>

        {!preSelectedCards && (
          <Accordion
            label="Customize quantities"
            containerClass={styles.customQuantities}
            className={styles.customQuantitiesTrigger}>
            {selectedCards.map(card => (
              <div key={card.id} className={styles.customQuantityCard}>
                <PhatInput
                  type="number"
                  defaultValue={card.qty}
                  containerClassName={styles.inputContainer}
                  className={styles.input}
                  onChange={value => handleUpdatedCustomQuantity(card.id, Number(value))}
                  selectOnFocus
                />
                <CursorCardWrapper card={card} className={styles.cardName}>
                  {card.name}
                </CursorCardWrapper>
              </div>
            ))}
          </Accordion>
        )}
      </div>
    </Modal>
  )
}

export default BuyBulkModal
